import React, { useState, useEffect } from "react";
import { formatPhoneNumber } from "../shared/formatter";
import PhoneInput from "../components/PhoneInput";
import { useNavigate } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Link as MuiLink,
  Typography,
  Grid,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import OrciBanner from "../components/OrciBanner"; // Assuming this is your logo component
import Footer from "../components/Footer";
import decodeJwtToken from "../auth/decodeJwtToken";
import { motion } from "framer-motion";
import { useBranch } from "../contexts/BranchContext";
import { useAuth } from "../contexts/AuthContext";
import swal from "sweetalert";
import { loginUser } from "../services/authService";
import { getPostAuthRoute } from "../shared/systemAccessRouting";

function Login({ onLogin }) {
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [errors, setErrors] = useState({});
  const { branch, setBranch } = useBranch();
  const { refreshUser } = useAuth();

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => event.preventDefault();
  const handleSubmit = async (event) => {
    event.preventDefault();
    setShowSpinner(true);
    setErrors({}); // Clear previous errors

    // --- Inline Validation ---
    const newErrors = {};
    if (!phoneNumber) {
      newErrors.phoneNumber = "Phone number is required.";
    }
    if (!password) {
      newErrors.password = "Password is required.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setShowSpinner(false);
      return;
    }

    const formattedPhone = formatPhoneNumber(phoneNumber);

    try {
      const data = await loginUser(formattedPhone, password);
      localStorage.setItem("accessToken", data.access);
      localStorage.setItem("refreshToken", data.refresh);
      const decodedToken = decodeJwtToken(data.access);

      refreshUser();

      onLogin();
      swal({
        text: "Successful Login!",
        icon: "success",
        title: "Auth",
        timer: 2000,
        buttons: false,
      }).then(() => {
        setPhoneNumber("");
        setPassword("");
        const allBranch = { id: "", name: "All" };
        const defaultBranch = ["General Manager", "Admin"].includes(
          decodedToken?.group?.name
        )
          ? allBranch
          : decodedToken?.branch;
        setBranch(defaultBranch);
        const nextRoute = getPostAuthRoute(decodedToken);
        const routeState = nextRoute === "/app/my-groups" ? { fromLogin: true } : undefined;
        navigate(nextRoute, routeState ? { state: routeState } : undefined);
      });
    } catch (error) {
      swal({
        title: "Login Failed",
        text: error.message,
        icon: "error",
        timer: 2000,
        buttons: false,
      });
    } finally {
      setShowSpinner(false);
    }
  };

  // Animation variants for framer-motion
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 },
  };

  return (
    <Grid container component="main" sx={{ minHeight: "100vh" }}>
      {/* LEFT Branding */}
      <Grid
        item
        xs={false}
        sm={4}
        md={7}
        sx={{
          background:
            "linear-gradient(135deg, #7a0635 0%, #4c061d 50%, #320414 100%)",
          color: "white",
          display: { xs: "none", sm: "flex" },
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          px: 6,
          py: 4,
        }}
      >
        <Box sx={{ maxWidth: 450 }}>
          <Typography
            variant="h3"
            sx={{ fontWeight: 800, lineHeight: 1.2, mb: 2 }}
          >
            Savvy Community
          </Typography>

          <Typography
            variant="h6"
            sx={{
              color: "rgba(255, 255, 255, 0.85)",
              fontWeight: 400,
              lineHeight: 1.6,
            }}
          >
            Modern Digital Banking Manage contributions, penalties, loans and group finances
            effortlessly with a secure, modern platform.
          </Typography>
        </Box>
      </Grid>

      {/* RIGHT LOGIN PANEL */}
      <Grid
        item
        xs={12}
        sm={8}
        md={5}
        component={Paper}
        elevation={0}
        square
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%237a0635' fill-opacity='0.04'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            px: { xs: 2, sm: 4 },
            py: { xs: 4, md: 6 },
          }}
        >
          {/* LOGO */}
          <motion.div variants={itemVariants}>
            <Paper
              elevation={6}
              sx={{
                p: 2,
                borderRadius: "50%",
                mb: 2,
                width: 120, // Slightly smaller for better proportion
                height: 120,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                bgcolor: "white",
              }}
            >
              <OrciBanner />
            </Paper>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Typography
              component="h1"
              variant="h5"
              sx={{ mt: 1, fontWeight: 700 }}
            >
              Welcome Back
            </Typography>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Login to continue managing your group
            </Typography>
          </motion.div>

          {/* FORM */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            style={{ width: "100%", maxWidth: 760 }}
          >
            <Paper
              elevation={4}
              sx={{
                width: "100%",
                p: { xs: 2, sm: 4 },
                borderRadius: "16px",
                background: "rgba(255,255,255,0.7)", // Refined glassmorphism
                backdropFilter: "blur(12px)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
              }}
            >
              <Box component="form" noValidate onSubmit={handleSubmit}>
                {/* PHONE */}
                <motion.div variants={itemVariants}>
                  <PhoneInput
                    margin="normal"
                    fullWidth
                    id="phone_number"
                    value={phoneNumber}
                    onChange={(val) => setPhoneNumber(val)}
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber}
                    required
                  />
                </motion.div>

                {/* PASSWORD */}
                <motion.div variants={itemVariants}>
                  <TextField
                    margin="normal"
                    variant="outlined"
                    required
                    fullWidth
                    id="password"
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    error={!!errors.password}
                    helperText={errors.password}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon color="primary" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Box textAlign="right" sx={{ my: 1 }}>
                    <MuiLink href="#" variant="body2" sx={{ fontWeight: 500 }}>
                      Forgot Password?
                    </MuiLink>
                  </Box>
                </motion.div>

                {/* LOGIN BUTTON */}
                <motion.div variants={itemVariants}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{
                      mt: 3,
                      mb: 2,
                      py: 1.4,
                      borderRadius: "10px",
                      fontWeight: "bold",
                      fontSize: "1rem",
                      bgcolor: "#7a0635",
                      "&:hover": { bgcolor: "#5c0427" },
                    }}
                    disabled={showSpinner}
                  >
                    {showSpinner ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Login"
                    )}
                  </Button>
                </motion.div>
              </Box>
            </Paper>
          </motion.div>

          <Footer />
        </Box>
      </Grid>
    </Grid>
  );

}

export default Login;
